import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { http } from "../lib/api";
import { BespokeBoard } from "../components/bespoke/BespokeBoard";

const STAGES = { draft: ["Draft", "bg-sky-100 text-sky-800"], review1: ["Customer review (1)", "bg-violet-100 text-violet-800"], revision: ["Revision", "bg-amber-100 text-amber-800"],
  review2: ["Customer review (2)", "bg-violet-100 text-violet-800"], finalise: ["Finalise", "bg-emerald-100 text-emerald-800"], complete: ["Complete", "bg-stone-200 text-stone-700"], declined: ["Declined", "bg-red-100 text-red-700"] };
const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "");

export default function BespokeKits() {
  const [rows, setRows] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("bespoke-view") || "list");
  const pick = (v) => { setView(v); localStorage.setItem("bespoke-view", v); };
  useEffect(() => {
    Promise.all([http.get("/kits"), http.get("/bespoke/requests")]).then(([k, r]) => {
      const reqs = Object.fromEntries(r.data.filter((x) => x.kit_id).map((x) => [x.kit_id, x]));
      setRows(k.data.filter((x) => x.bespoke?.name).map((x) => ({ k: x, r: reqs[x.id] })));
    }).catch(() => setRows([]));
  }, []);
  return (
    <div data-testid="bespoke-kits-page" className="p-8 lg:p-12 max-w-[1800px]">
      <div className="eyebrow mb-2">Customer kits</div>
      <h1 className="font-display text-4xl text-[#2B2A26] mb-2">Bespoke Kits</h1>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <p className="text-sm text-stone-500">Every kit built for a customer, with its review stage. Manage the conversation in Bespoke Requests.</p>
        <div className="inline-flex rounded-full border border-stone-200 bg-white p-1 text-xs font-bold uppercase">
          {["list", "board"].map((v) => (
            <button key={v} data-testid={`bespoke-view-${v}`} onClick={() => pick(v)} className={`rounded-full px-4 py-1.5 transition-colors ${view === v ? "bg-[#2B2A26] text-white" : "text-stone-500 hover:text-stone-800"}`}>{v}</button>
          ))}
        </div>
      </div>
      {!rows ? <Loader2 className="animate-spin text-[#9A7B2C]" /> : !rows.length ? (
        <p data-testid="bespoke-kits-empty" className="text-stone-500">No bespoke kits yet. Build one from a request in Bespoke Requests.</p>
      ) : view === "board" ? <BespokeBoard rows={rows} setRows={setRows} /> : (
        <div className="rounded-xl border border-stone-200 bg-white max-w-6xl">
          {rows.map(({ k, r }) => {
            const [label, cls] = STAGES[r?.stage || "draft"] || STAGES.draft;
            return (
              <Link key={k.id} to={`/kits/${k.id}`} data-testid={`bespoke-kit-${k.id}`} className="flex items-center gap-4 border-b border-stone-100 px-5 py-4 last:border-0 hover:bg-stone-50">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-xl text-[#2B2A26]">{k.destination}</div>
                  <div className="text-xs text-stone-500">For {k.bespoke.name}{r?.start_date ? ` · ${fmt(r.start_date)} to ${fmt(r.end_date)}` : ""} · {k.edition} · {k.days ? `${k.days} days` : k.tier}</div>
                </div>
                <span className="text-xs text-stone-500">{k.status === "review" ? "Built" : k.status}</span>
                <span data-testid={`bespoke-kit-stage-${k.id}`} className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase ${cls}`}>{label}</span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
